/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { useAppSelector } from '../../store/Store'
import { selectAssetFamilies, selectAssetAreas } from '../Asset.slice'
import {
  useAssetDeleteByIdMutation,
  useAssetGetByIdMutation,
  useAssetFamilyGetAllMutation,
  useAssetAreaGetAllMutation
} from '../Asset.api'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { DropdownMenu, SettingModalOption, SettingOption } from '../../../common/component/DropdownMenu'
import { IAssetDto } from '../Asset.model'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { DeleteConfirmationModal } from '../../../common/component/modal/DeleteConfirmationModal'
import { ReportRenderer } from '../../../common/component/ReportRenderer'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { parseLocalDate } from '../../../util/Locale'
import { IReportFilter } from '../../report/Report.model'
import { useReportLoadMoreMutation, useReportSearchMutation } from '../../report/Report.api'
import { selectReports, selectReportsPagination } from '../../report/Report.slice'
import { ListContent } from '../../../common/component/ListContent'
import { ReportInnerListItem } from '../../report/components/ReportInnerListItem'
import { renderIdentifier } from '../../../util/Constants'
import { Spinner } from '../../../common/component/Spinner'
import { ClientEmbedered } from '../../client/components/ClientEmbedered'
import { ContractEmbedered } from '../../contract/components/ContractEmbedered'

const AssetDetailsPage: FC = () => {
    const intl = useIntl()
    const navigate = useNavigate()
    const {assetId} = useParams()
    const breadCrumbs: Array<PageLink> = [
      {
        title: intl.formatMessage({id: 'BREADCRUMBS.ASSETS'}),
        path: '/assets',
        isSeparator: false,
        isActive: false,
      },
    ]
    const settingOptions: SettingOption[] = [
      {
        label: intl.formatMessage({id: 'UTIL.EDIT'}),
        icon: 'bi-pencil',
        action: () => navigate(`/assets/${assetId}/edit`),
      },
      {
        label: intl.formatMessage({id: 'UTIL.CREATE.REPORT'}),
        icon: 'bi-speedometer',
        action: () => navigate(`/reports/new?assetId=${assetId}`),
      },
      {
        label: intl.formatMessage({id: 'UTIL.CREATE.TICKET'}),
        icon: 'bi-wrench-adjustable-circle',
        action: () => navigate(`/tickets/new?assetId=${assetId}`),
      },
    ]
    const removeSettingOption: SettingModalOption = {
      label: intl.formatMessage({id: 'UTIL.DELETE'}),
      targetModal: '#asset_delete_confirmation_modal',
    }
    
    
    const [loadError, setLoadError] = useState<string | undefined>(undefined)
    const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
    const [openDropDown, setOpenDropDown] = useState(false)

    const didRequestDelete = useRef(false)
    const didFetchAsset = useRef(false)
    const [isAssetLoading, setIsAssetLoading] = useState(true)
    const [asset, setAsset] = useState<IAssetDto>()
    const [assetGetById] = useAssetGetByIdMutation()
    const [assetDeleteById] = useAssetDeleteByIdMutation()

    const assetFamilies = useAppSelector(selectAssetFamilies)
    const assetAreas = useAppSelector(selectAssetAreas)
    const [assetFamiliesGetAll] = useAssetFamilyGetAllMutation()
    const [assetAreasGetAll] = useAssetAreaGetAllMutation()

    useEffect(() => {
      if (toastEntry) {
        const [messageType] = toastEntry
        const toastElement = document.getElementById('#CustomToast')
        const delay =
          messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
        if (toastElement) {
          toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
          const toast = new Toast(toastElement, {autohide: true, delay})
          toast.show()
        }
      }
    }, [toastEntry])

    useEffect(() => {
      try {
        if (!assetFamilies || assetFamilies.length === 0) {
          assetFamiliesGetAll()
        }
        if (!assetAreas || assetAreas.length === 0) {
          assetAreasGetAll()
        }

        const fetchAsset = async (assetId) => {
          try {
            if (!didFetchAsset.current) {
              setIsAssetLoading(true)
              const response: any = await assetGetById({id: assetId})
              if (response && response.error) {
                setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
              } else {
                const asset: IAssetDto = response.data
                setAsset(asset)
              }
            }
          } catch (e: any) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } finally {
            setIsAssetLoading(false)
          }
        }
        
        if (assetId) {
          fetchAsset(assetId)
          fetchReportData(assetId)
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
      }
    }, [])

    const deleteConfirmOnClickHandler = async () => {
      try {
        if (assetId && !didRequestDelete.current) {
          setIsAssetLoading(true)
          const response: any = await assetDeleteById(assetId)
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/assets`)
          }
        }
      } catch (e) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsAssetLoading(false)
      }
    }

    //--------------------------------
    const didFetchNextPage = useRef(false)
    const didFetchSearch = useRef(false)
  
    const [reportSearch] = useReportSearchMutation()
    const [reportLoadMore] = useReportLoadMoreMutation()
  
    const reports = useAppSelector(selectReports)
    const reportsPagination = useAppSelector(selectReportsPagination)
  
    const [isSearchLoading, setIsSearchLoading] = useState(false)
  
    const fetchReportData = async (assetId: string) => {
      try {
        setIsSearchLoading(true)
        if (!didFetchSearch.current) {
          await reportSearch({
            filters: [{id: IReportFilter.ASSET_HASH, value: assetId, editable: false}],
          })
        }
      } finally {
        setIsSearchLoading(false)
      }
    }
  
    const nextPageOnClickHandler = async () => {
      try {
        setIsSearchLoading(true)
        if (!didFetchNextPage.current && assetId) {
          await reportLoadMore({
            pagination: {
              limit: reportsPagination?.limit,
              sort: reportsPagination?.sort,
              page: reportsPagination?.page,
            },
            filters: [{id: IReportFilter.ASSET_HASH, value: assetId, editable: false}],
          })
        }
      } finally {
        setIsSearchLoading(false)
      }
    }

    return (
      <>
        {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
        <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
        <div className='card card-flush card-stretch-100 card-bordered mt-5'>
          <div className='card-body'>
            {loadError && (
              <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
                <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                  <i className='bi bi-database-exclamation fs-2'></i>
                </span>

                <div className='d-flex flex-column'>
                  <span>{loadError}</span>
                </div>

                <button
                  type='button'
                  className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                  data-bs-dismiss='alert'
                >
                  <div className='badge px-3 py-2'>
                    <span className='text-danger fs-1'>&times;</span>
                  </div>
                </button>
              </div>
            )}

            <div className='d-flex flex-row'>
              <div className='d-flex flex-column flex-row-fluid'>
                <div className='d-flex flex-row flex-column-fluid'>
                  <div className='d-flex flex-row-fluid flex-start'>
                    {asset && (
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fs-2 fw-bolder'>{asset?.displayName}</span>
                        <span className='fw-bolder text-primary me-1 fs-5'>
                          {renderIdentifier(asset.sequence)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className='d-flex flex-row-auto flex-start ms-5'>
                    {asset && (
                      <>
                        <button
                          className='btn btn-sm btn-light-primary me-2'
                          onClick={() => setOpenDropDown((prev) => !prev)}
                          disabled={isAssetLoading}
                        >
                          <i className='bi bi-three-dots fs-5'></i>
                        </button>
                        <DropdownMenu
                          visible={openDropDown}
                          setOpenDropDown={setOpenDropDown}
                          settingOptions={settingOptions}
                          deleteOption={removeSettingOption}
                        ></DropdownMenu>
                      </>
                    )}

                    <button className='btn btn-sm btn-light' onClick={() => navigate(`/assets`)}>
                      <i className='bi bi-x-lg'></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {isAssetLoading ? (
              <Spinner />
            ) : !asset ? (
              <div className='row'>
                <div className='d-flex flex-row-fluid flex-start mt-10'>
                  <span className='text-danger '>
                    <div className='d-flex flex-row justify-content-start align-items-center'>
                      <span className='fs-6 me-1'>
                        <i className='text-danger me-1 bi bi-exclamation-octagon' />
                        {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                      </span>
                    </div>
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                    <li className='nav-item'>
                      <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                        {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                      </a>
                    </li>
                    {asset.metadata && asset.metadata.items && (
                      <li className='nav-item'>
                        <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                          {intl.formatMessage({id: 'UTIL.DETAILS'})}
                        </a>
                      </li>
                    )}
                    {reports && reports.length > 0 && (
                      <li className='nav-item'>
                        <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_5'>
                          {intl.formatMessage({id: 'UTIL.REPORTS'})}
                        </a>
                      </li>
                    )}
                  </ul>

                  <div className='tab-content mt-10' id='myTabContent'>
                    <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                      <div className='row mb-5'>
                        <div className='px-5'>
                          <ClientEmbedered client={asset.client} />
                          {asset.contract && <ContractEmbedered contract={asset.contract} />}
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                              value={parseLocalDate(
                                asset.createdAt,
                                intl.formatMessage({id: 'I18N.LOCALE'})
                              )}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                              value={parseLocalDate(
                                asset.updatedAt,
                                intl.formatMessage({id: 'I18N.LOCALE'})
                              )}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ID'})}
                              value={`${asset.sequence}`}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                              value={asset.displayName}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ASSET_FAMILY'})}
                              value={asset.family}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ASSET_AREA'})}
                              value={asset.area}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ASSET_BRAND'})}
                              value={asset.brand}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ASSET_MODEL'})}
                              value={asset.model}
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ASSET_SERIAL'})}
                              value={asset.serial}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                              value={
                                asset.countryCode
                                  ? intl.formatMessage({
                                      id: `DROPDOWN.COUNTRY.${asset.countryCode}`,
                                    })
                                  : ''
                              }
                            ></CustomInputRenderer>

                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                              value={asset.address}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>

                      <div className='row mb-5'>
                        <SubSectionHeader
                          separator={false}
                          title={intl.formatMessage({id: 'UTIL.METRICS'})}
                        />

                        <div className='px-5'>
                          <div className='row mb-5'>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.REPORTS'})}
                              value={`${asset.totalReport || 0}`}
                            ></CustomInputRenderer>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                      {asset.metadata && asset.metadata.items && (
                        <ReportRenderer
                          collection={asset.metadata.items}
                          path={`metadata.items`}
                          disabled={true}
                        />
                      )}
                    </div>
                    <div className='tab-pane fade' id='kt_tab_pane_5' role='tabpanel'>
                      <div className='row mb-5'>
                        <ListContent
                          loading={isSearchLoading}
                          nextPage={reportsPagination?.page}
                          nextPageOnClickHandler={nextPageOnClickHandler}
                          listComponent={reports.map((item, index) => (
                            <div key={index}>
                              <ReportInnerListItem data={item}></ReportInnerListItem>
                              <div className='separator'></div>
                            </div>
                          ))}
                        ></ListContent>
                      </div>
                    </div>
                  </div>
                </div>

                <DeleteConfirmationModal
                  id='asset_delete_confirmation_modal'
                  title={intl
                    .formatMessage({id: 'UTIL.DELETE_CONFIRMATION.TITLE'})
                    .replace(/%s/g, intl.formatMessage({id: 'UTIL.ASSET'}))}
                  description={`${asset?.gui} - #${asset?.sequence}`}
                  confirmOnClickHandler={deleteConfirmOnClickHandler}
                ></DeleteConfirmationModal>
              </>
            )}
          </div>
        </div>
      </>
    )
}

export {AssetDetailsPage}
